
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';

export interface SecuritySettings {
  twoFactorAuth: boolean;
  passwordExpiration: number; // days
  loginAttempts: number;
  sessionTimeout: number; // minutes
  ipRestriction: boolean;
  allowedIPs: string[];
}

interface SecuritySettingsTabProps {
  settings?: SecuritySettings;
  onSettingChange?: (key: keyof SecuritySettings, value: any) => void;
  onSave?: () => void;
}

const defaultSettings: SecuritySettings = {
  twoFactorAuth: true,
  passwordExpiration: 90,
  loginAttempts: 5,
  sessionTimeout: 30,
  ipRestriction: false,
  allowedIPs: [],
};

const MasterLicenseSecurityTab: React.FC<SecuritySettingsTabProps> = ({ 
  settings = defaultSettings,
  onSettingChange,
  onSave
}) => {
  const { toast } = useToast();
  const [localSettings, setLocalSettings] = useState<SecuritySettings>(settings);
  const [newIP, setNewIP] = useState<string>('');

  const handleSettingChange = (key: keyof SecuritySettings, value: any) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
    onSettingChange?.(key, value);
  };

  const addIP = () => {
    if (!newIP.trim()) return;
    
    // Simple IP validation
    const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
    if (!ipRegex.test(newIP)) {
      toast({
        title: "Invalid IP format",
        description: "Please enter a valid IPv4 address",
        variant: "destructive"
      });
      return;
    }
    
    const updatedIPs = [...localSettings.allowedIPs, newIP];
    handleSettingChange('allowedIPs', updatedIPs);
    setNewIP('');
  };

  const removeIP = (ip: string) => {
    const updatedIPs = localSettings.allowedIPs.filter(item => item !== ip);
    handleSettingChange('allowedIPs', updatedIPs);
  };

  const handleSave = () => {
    toast({
      title: "Settings Saved",
      description: "Your security settings have been updated",
    });
    onSave?.();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Authentication Settings</CardTitle>
          <CardDescription>Configure security authentication options for your system</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="two-factor" className="font-medium">Two Factor Authentication (2FA)</Label>
              <p className="text-sm text-gray-500">Require users to verify their identity with a second factor</p>
            </div>
            <Switch 
              id="two-factor" 
              checked={localSettings.twoFactorAuth}
              onCheckedChange={(checked) => handleSettingChange('twoFactorAuth', checked)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password-expiration">Password Expiration (days)</Label>
            <div className="flex items-center space-x-4">
              <Slider
                id="password-expiration"
                min={0}
                max={180}
                step={1}
                value={[localSettings.passwordExpiration]}
                onValueChange={(value) => handleSettingChange('passwordExpiration', value[0])}
                className="flex-grow"
              />
              <span className="text-sm font-medium w-10 text-right">
                {localSettings.passwordExpiration === 0 ? 'Never' : localSettings.passwordExpiration}
              </span>
            </div>
            <p className="text-xs text-gray-500">Set to 0 for no expiration</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-attempts">Max Failed Login Attempts</Label>
            <Select
              value={localSettings.loginAttempts.toString()}
              onValueChange={(value) => handleSettingChange('loginAttempts', parseInt(value))}
            >
              <SelectTrigger id="login-attempts">
                <SelectValue placeholder="Select attempts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 attempts</SelectItem>
                <SelectItem value="5">5 attempts</SelectItem>
                <SelectItem value="10">10 attempts</SelectItem>
                <SelectItem value="0">Unlimited</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="session-timeout">Session Timeout (minutes)</Label>
            <Select
              value={localSettings.sessionTimeout.toString()}
              onValueChange={(value) => handleSettingChange('sessionTimeout', parseInt(value))}
            >
              <SelectTrigger id="session-timeout">
                <SelectValue placeholder="Select timeout" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">60 minutes</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="0">Never timeout</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>IP Restrictions</CardTitle>
          <CardDescription>Limit access to specific IP addresses</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="ip-restriction" className="font-medium">Enable IP Restrictions</Label>
              <p className="text-sm text-gray-500">Only allow login from specified IP addresses</p>
            </div>
            <Switch 
              id="ip-restriction" 
              checked={localSettings.ipRestriction}
              onCheckedChange={(checked) => handleSettingChange('ipRestriction', checked)}
            />
          </div>

          {localSettings.ipRestriction && (
            <div className="space-y-2">
              <Label>Allowed IP Addresses</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="Enter IP address"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                  className="flex-grow"
                />
                <Button 
                  onClick={addIP} 
                  type="button"
                  variant="secondary"
                >
                  Add
                </Button>
              </div>

              {localSettings.allowedIPs.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {localSettings.allowedIPs.map((ip) => (
                    <div key={ip} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                      <span>{ip}</span>
                      <Button 
                        onClick={() => removeIP(ip)} 
                        size="sm"
                        variant="ghost"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 mt-2">No IP addresses added yet</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave}>Save Security Settings</Button>
      </div>
    </div>
  );
};

export default MasterLicenseSecurityTab;
